/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ButtonBar from './ButtonBar';
import Table from './Table';
import getTableLayout from './tableLayouts';
import getButtonsToShow from './getButtonsToShow';
import TableFilterDialog from './TableFilterDialog';
import {
  DEFAULT_TABLE_FILTERS,
  normalizeTableFilters,
  getActiveTableFilterCount,
  applyTableFilters,
} from './tableFilters';

// applies category-specific changes to data and passes them onwards;
// manages table layout
// props usage in propTypes below
export default function TableLayout({ account, data, tableState, setTableState, displayedData, displayedComponent, handleClick, handleSync, highlightTransactionId, accountSyncStatus }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'tableFilters' });
  const tableLayout = getTableLayout(account.category, displayedData);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const currentFilters = normalizeTableFilters(tableState?.filters || DEFAULT_TABLE_FILTERS);
  const activeFilterCount = getActiveTableFilterCount(currentFilters);

  const sourceData = data || [];
  const filteredData = useMemo(() => applyTableFilters(sourceData, currentFilters), [sourceData, currentFilters]);

  function handleOpenFilterDialog() {
    setFilterDialogOpen(true);
  }

  function handleCloseFilterDialog() {
    setFilterDialogOpen(false);
  }

  function handleApplyFilters(newFilters) {
    setTableState({
      ...tableState,
      filters: normalizeTableFilters(newFilters),
    });
    setFilterDialogOpen(false);
  }

  function handleClearFilters() {
    setTableState({
      ...tableState,
      filters: { ...DEFAULT_TABLE_FILTERS },
    });
  }

  return (
    <>
      <ButtonBar
        buttonsToShow={getButtonsToShow(account.category, displayedComponent, displayedData)}
        displayedComponent="table"
        displayedData={displayedData}
        handleClick={handleClick}
        handleSync={handleSync}
        userChangesPresent={false}
        accountSyncStatus={accountSyncStatus}
        showTableFilters={sourceData.length > 0}
        activeTableFilterCount={activeFilterCount}
        onOpenTableFilters={handleOpenFilterDialog}
      />
      {activeFilterCount > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span className="rounded-full bg-brandBlue-50 px-2.5 py-1 text-brandBlue-700">
            {t('results', { shown: filteredData.length, total: sourceData.length })}
          </span>
          <button
            type="button"
            onClick={handleClearFilters}
            className="rounded-full border border-gray-300 bg-white px-2.5 py-1 font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brandBlue-200"
          >
            {t('clearAll')}
          </button>
        </div>
      )}
      <Table data={filteredData} tableLayout={tableLayout} tableState={tableState} setTableState={setTableState} highlightTransactionId={highlightTransactionId} />
      <TableFilterDialog open={filterDialogOpen} currentFilters={currentFilters} onApply={handleApplyFilters} onCancel={handleCloseFilterDialog} />
    </>
  );
}
TableLayout.propTypes = {
  account: PropTypes.object.isRequired, // to select table layout, sync function, buttons to show; also provided as context to BlueTransactionsDialog and TransactionContextMenu
  data: PropTypes.array.isRequired, // array of objects (each object should have exactly the same keys as there are columns in the table + an id)
  tableState: PropTypes.object.isRequired, // contains column widths, sorting, filtering, etc., has to be passed to e.g. TableHeaders to be able to change it
  setTableState: PropTypes.func.isRequired,
  displayedData: PropTypes.string.isRequired, // switch between transactions and quotes
  displayedComponent: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  handleSync: PropTypes.func, // passed from CategoryWrapper to ButtonBar
  highlightTransactionId: PropTypes.string, // coming from search function, hightlights a transaction in the table
  accountSyncStatus: PropTypes.bool, // used to disable sync button
};
TableLayout.defaultProps = {
  handleSync: () => {},
  accountSyncStatus: false,
  highlightTransactionId: null,
};
